/** Fitting the world to the device by measurement (spec section "Fitting speed, once per device"). Pure. */
export const SETTLE_S = 4, WINDOW_S = 3, HITCH_S = 0.1, MAX_STEPS = 12;
/** Tolerance over the budget: a 60 Hz display shows a 30 fps frame as 33.3 or 33.4 ms, which is on budget, not over. */
const OVER = 1.15;
export type FitVerdict = 'down' | 'up' | 'final';

const p90 = (a: number[]) => { const s = [...a].sort((x, y) => x - y); return s[Math.min(s.length - 1, Math.floor(s.length * 0.9))] ?? Infinity; };

/** Menu fit: windows of frame intervals after a settle; over budget -> down, under 70 % -> up, else final. */
export class MenuFit {
  private settled = 0;
  private time = 0;
  private frames: number[] = [];
  private steps = 0;
  /** The last window's 90th percentile frame time (s), for logs. */
  lastP = 0;
  constructor(readonly targetFps: 30 | 60) {}
  get budgetS(): number { return 1 / this.targetFps; }

  sample(dt: number): FitVerdict | null {
    if (dt <= 0 || dt > HITCH_S) return null;
    if (this.settled < SETTLE_S) { this.settled += dt; return null; }
    this.time += dt; this.frames.push(dt);
    if (this.time < WINDOW_S) return null;
    const p = p90(this.frames);
    this.lastP = p;
    this.time = 0; this.frames = [];
    if (this.steps >= MAX_STEPS) return 'final';
    if (p > this.budgetS * OVER) return 'down';
    if (p < this.budgetS * 0.7) return 'up';
    return 'final';
  }

  /** The caller rebuilt a module: settle again before the next window. */
  applied(): void { this.steps++; this.settled = 0; this.time = 0; this.frames = []; }
}

/** Ride strikes: a ride whose windows (after a 10 s warm-up, hitches ignored) are mostly over budget is a strike; two in a row = true. */
export class RideStrikes {
  private strikes = 0;
  private elapsed = 0;
  private time = 0;
  private frames: number[] = [];
  private over = 0;
  private windows = 0;
  constructor(readonly targetFps: 30 | 60, readonly warmupS = 10) {}

  start(): void { this.elapsed = 0; this.time = 0; this.frames = []; this.over = 0; this.windows = 0; }

  sample(dt: number): void {
    if (dt <= 0 || dt > HITCH_S) return;
    this.elapsed += dt;
    if (this.elapsed < this.warmupS) return;
    this.time += dt; this.frames.push(dt);
    if (this.time < WINDOW_S) return;
    this.windows++;
    if (p90(this.frames) > (1 / this.targetFps) * OVER) this.over++;
    this.time = 0; this.frames = [];
  }

  /** True when this ride is the second strike in a row (the next menu should step down). */
  end(): boolean {
    const strike = this.windows >= 2 && this.over / this.windows >= 0.75;
    this.strikes = strike ? this.strikes + 1 : 0;
    if (this.strikes >= 2) { this.strikes = 0; return true; }
    return false;
  }
}
